import React, { useMemo } from 'react'
import Autocomplete, { AutocompleteProps } from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import { FilterOptionsState, createFilterOptions } from '@mui/base'
import Popper from '@mui/material/Popper'
import { styled } from '@mui/material/styles'
import { newRandomUUID } from '@cbgms/base/utils/uuid'

export interface CountryDropdownItem {
  key: string
  value: string
  flag: string | null
  text: string
}
interface ICountrySelectProps {
  countryOptions: CountryDropdownItem[]
  showKeyLabelOption?: boolean
  preserveOptionsOrder?: boolean
  name: string
  value: string
  label?: string
  required?: boolean
  textFieldInputProps?: React.ComponentProps<typeof TextField>
}

interface Props
  extends ICountrySelectProps,
    Omit<
      AutocompleteProps<CountryDropdownItem, undefined, undefined, undefined>,
      'renderInput' | 'renderOption' | 'options' | 'getOptionLabel' | 'getOptionSelected' | 'filterOptions' | 'value'
    > {}

const StyledPopper = styled(Popper)({
  minWidth: '300px'
})

const filterOptions = createFilterOptions({
  stringify: (option: CountryDropdownItem) => `${option.key}_${option.text}`
})

const sortedFilterOptions = (
  options: CountryDropdownItem[],
  state: FilterOptionsState<CountryDropdownItem>,
  preserveOptionsOrder = false
) => {
  const result = filterOptions(options, state)
  if (preserveOptionsOrder && !state.inputValue) {
    return result
  }

  return result.sort((r1, r2) => {
    if (state.inputValue) {
      // sort by country shortcut first, then alphabetically
      if (r1.value.toLocaleUpperCase().startsWith(state.inputValue.toLocaleUpperCase())) return -1
      if (r2.value.toLocaleUpperCase().startsWith(state.inputValue.toLocaleUpperCase())) return 1
    }

    return r1.text.localeCompare(r2.text)
  })
}

export const countryCodeToEmojiFlag = (code: string | null) => {
  if (!code) {
    return ''
  }
  return String.fromCodePoint(
    ...code
      .toUpperCase()
      .split('')
      .map(char => 127397 + char.charCodeAt(0))
  )
}

export const CountrySelect: React.FC<Props> = ({
  countryOptions,
  name,
  label,
  required,
  value,
  onChange,
  textFieldInputProps,
  preserveOptionsOrder = false,
  showKeyLabelOption = false,
  ...rest
}) => {
  const id = useMemo(() => {
    return `country-select-${name}-${newRandomUUID()}`
  }, [name])
  const getOptionSelected = (option: CountryDropdownItem, value: CountryDropdownItem): boolean => {
    return option.key === value.key
  }
  const getOptionLabel = (option: CountryDropdownItem) => {
    return showKeyLabelOption ? option.key : option.text || ''
  }

  const optionValue = countryOptions?.find(option => option.value === value || option.text === value) ?? null
  return (
    <Autocomplete
      id={id}
      onChange={onChange}
      size='small'
      filterOptions={(options, state) => sortedFilterOptions(options, state, preserveOptionsOrder)}
      isOptionEqualToValue={getOptionSelected}
      getOptionLabel={getOptionLabel}
      value={optionValue}
      options={countryOptions}
      PopperComponent={StyledPopper}
      componentsProps={{ popper: { placement: 'bottom-start' } }}
      autoHighlight
      renderOption={(props, option: CountryDropdownItem) => {
        return (
          <li {...props}>
            <Box
              sx={{
                fontSize: 15,
                whiteSpace: 'nowrap',
                padding: 0.5,
                '& > span': {
                  display: 'inline-block',
                  marginRight: 1,
                  fontSize: 18,
                  minWidth: 26 /* need a min-width to align items neatly */
                }
              }}
            >
              <span className='emoji-flag'>{countryCodeToEmojiFlag(option.flag) || '\u00A0'}</span>
              {option.text} ({option.key})
            </Box>
          </li>
        )
      }}
      renderInput={params => {
        return (
          <TextField
            {...params}
            {...textFieldInputProps}
            required={required}
            label={label}
            variant='outlined'
            autoComplete='off'
            inputProps={{
              name,
              ...params.inputProps,
              autoComplete: 'off', // disable autocomplete and autofill
              type: 'search'
            }}
          />
        )
      }}
      {...rest}
    />
  )
}
